import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import {
  PreviewToolbarState,
  PreviewZoomMode,
} from "src/app/shared/directive/preview-toolbar.directive";

export type PreviewToolbarAction =
  | "zoomOut"
  | "zoomIn"
  | "toggleHighlight"
  | "rotateLeft"
  | "rotateRight"
  | "print"
  | "download";

export type PreviewZoomPreset =
  | "automatic"
  | "actualSize"
  | "pageFit"
  | "pageWidth"
  | "0.5"
  | "0.75"
  | "1"
  | "1.25"
  | "1.5"
  | "2"
  | "3"
  | "4";

interface PreviewZoomOption {
  value: PreviewZoomPreset;
  label: string;
}

@Component({
  selector: "app-preview-toolbar",
  templateUrl: "./preview-toolbar.component.html",
  styleUrls: ["./preview-toolbar.component.scss"],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewToolbarComponent {
  @Input({ required: true }) previewState!: PreviewToolbarState;

  @Output() toolbarAction = new EventEmitter<PreviewToolbarAction>();
  @Output() zoomPresetChange = new EventEmitter<PreviewZoomPreset>();

  readonly MESSAGES = MESSAGES;
  readonly zoomOptions: PreviewZoomOption[] = [
    {
      value: "automatic",
      label: MESSAGES.DOCX_PREVIEW.AUTOMATIC_ZOOM_LABEL,
    },
    {
      value: "actualSize",
      label: MESSAGES.DOCX_PREVIEW.ACTUAL_SIZE_LABEL,
    },
    {
      value: "pageFit",
      label: MESSAGES.DOCX_PREVIEW.PAGE_FIT_LABEL,
    },
    {
      value: "pageWidth",
      label: MESSAGES.DOCX_PREVIEW.PAGE_WIDTH_LABEL,
    },
    { value: "0.5", label: "50%" },
    { value: "0.75", label: "75%" },
    { value: "1", label: "100%" },
    { value: "1.25", label: "125%" },
    { value: "1.5", label: "150%" },
    { value: "2", label: "200%" },
    { value: "3", label: "300%" },
    { value: "4", label: "400%" },
  ];

  emitToolbarAction(action: PreviewToolbarAction): void {
    this.toolbarAction.emit(action);
  }

  emitZoomPresetChange(value: string): void {
    if (value === "current") {
      return;
    }

    this.zoomPresetChange.emit(value as PreviewZoomPreset);
  }

  get selectedZoomPreset(): PreviewZoomPreset | "current" {
    switch (this.previewState.zoomMode) {
      case "automatic":
      case "actualSize":
      case "pageFit":
      case "pageWidth":
        return this.previewState.zoomMode;
      case "custom":
      default:
        return this.getMatchingPercentageOption() ?? "current";
    }
  }

  get showCurrentZoomOption(): boolean {
    return this.selectedZoomPreset === "current";
  }

  get currentZoomPercentageLabel(): string {
    return `${Math.round(this.getCurrentZoomScale() * 100)}%`;
  }

  private getMatchingPercentageOption(): PreviewZoomPreset | null {
    const currentScale = this.getCurrentZoomScale();
    const matchingOption = this.zoomOptions.find((option) => {
      return Math.abs(Number(option.value) - currentScale) < 0.0001;
    });

    return matchingOption?.value ?? null;
  }

  private getCurrentZoomScale(): number {
    return this.getZoomScaleForMode(this.previewState.zoomMode);
  }

  private getZoomScaleForMode(zoomMode: PreviewZoomMode): number {
    switch (zoomMode) {
      case "actualSize":
        return 1;
      case "custom":
        return this.previewState.zoomLevel;
      case "automatic":
      case "pageFit":
      case "pageWidth":
      default:
        return this.previewState.fitScale;
    }
  }
}